import { useEffect, useMemo, useState } from 'react';
import { CheckCircle2, XCircle, CircleDashed, MinusCircle, ChevronDown, ChevronRight, Download } from 'lucide-react';
import { api, type DeployFacts } from '../../api.js';
import { Sha, Badge } from '../../ui/kit.js';

// The insight instruments on a job: the live CI shard grid with predictions
// and an honest ETA, the phase timeline against typical runs, the per-file
// diff, the cost breakdown, the screenshot gallery with lint verdicts and a
// before/after compare, and workshop artifacts with the report inline.

// ── CI shard grid ───────────────────────────────────────────────────────────
export interface GridLane {
  name: string;
  state: 'queued' | 'running' | 'green' | 'red' | 'skipped';
  elapsedMin: number;
  predictedMin: number | null;
  url: string | null;
}
export interface Grid {
  headSha: string | null;
  hasCi?: boolean;
  shardTotal: number;
  shards: (GridLane & { n: number })[];
  fastLane: GridLane | null;
  build: GridLane | null;
  prShots: GridLane | null;
  mergeGate: GridLane | null;
  overallPct: number;
  etaMin: number;
}

function laneIcon(state: GridLane['state'], size = 14) {
  switch (state) {
    case 'green':
      return <CheckCircle2 size={size} color="var(--ops-ok)" />;
    case 'red':
      return <XCircle size={size} color="var(--ops-bad)" />;
    case 'running':
      return <CircleDashed size={size} color="var(--ops-run)" className="ops-rotate" />;
    case 'skipped':
      return <MinusCircle size={size} color="var(--ops-wait)" />;
    default:
      return <CircleDashed size={size} color="var(--ops-wait)" />;
  }
}

export function ShardGrid(props: { jobId: string; grid: Grid }) {
  const { grid } = props;
  const [openRed, setOpenRed] = useState<string | null>(null);
  const [excerpt, setExcerpt] = useState<{ name: string; failedStep: string | null; excerpt: string } | null>(null);

  useEffect(() => {
    if (!openRed) return setExcerpt(null);
    setExcerpt({ name: openRed, failedStep: null, excerpt: 'Reading the log…' });
    void api
      .get<{ failedStep: string | null; excerpt: string }>(
        `/api/jobs/${props.jobId}/ci/red-excerpt?name=${encodeURIComponent(openRed)}`,
      )
      .then((r) => setExcerpt({ name: openRed, ...r }))
      .catch(() => setExcerpt({ name: openRed, failedStep: null, excerpt: 'Could not read the log. Open the check on GitHub.' }));
  }, [openRed, props.jobId]);

  const done = grid.overallPct >= 100;
  return (
    <div className="ops-card" data-testid="shard-grid">
      <div className="ops-gridbar">
        <div className="ops-gridbar__track">
          <div className="ops-gridbar__fill" style={{ width: `${grid.overallPct}%` }} />
        </div>
        <span className="ops-gridbar__pct">{grid.overallPct}%</span>
        <span className="ops-gridbar__eta">{done ? 'done' : grid.etaMin > 0 ? `~${grid.etaMin} min left` : ''}</span>
      </div>
      <div className="ops-shardgrid">
        {grid.shards.map((s) => (
          <button
            key={s.n}
            type="button"
            className={`ops-shardcell ops-shardcell--${s.state}`}
            title={`${s.name}: ${s.state}, ${s.elapsedMin} of ~${s.predictedMin ?? '?'} min`}
            onClick={() => (s.state === 'red' ? setOpenRed(openRed === s.name ? null : s.name) : s.url && window.open(s.url, '_blank'))}
          >
            <span className="ops-shardcell__n">{s.n}</span>
            <span className="ops-shardcell__t">
              {s.state === 'queued' ? '—' : `${Math.round(s.elapsedMin)}${s.predictedMin ? `/${Math.round(s.predictedMin)}` : ''}`}
            </span>
          </button>
        ))}
      </div>
      <div className="ops-lanes">
        {[grid.fastLane, grid.build, grid.prShots, grid.mergeGate].map((l) =>
          l ? (
            <button
              key={l.name}
              type="button"
              className="ops-lane"
              onClick={() => (l.state === 'red' ? setOpenRed(openRed === l.name ? null : l.name) : l.url && window.open(l.url, '_blank'))}
            >
              {laneIcon(l.state)}
              <span className="ops-lane__name">{l.name}</span>
              <span className="ops-lane__t">
                {l.state === 'queued' ? '' : `${Math.round(l.elapsedMin)} min`}
              </span>
            </button>
          ) : null,
        )}
      </div>
      {excerpt ? (
        <div className="ops-redbox" data-testid="red-excerpt">
          <div className="ops-redbox__head">
            <b>{excerpt.name}</b>
            {excerpt.failedStep ? <span> · failed at: {excerpt.failedStep}</span> : null}
          </div>
          <pre>{excerpt.excerpt}</pre>
        </div>
      ) : null}
    </div>
  );
}

// ── Phase timeline ──────────────────────────────────────────────────────────
export function PhaseTimeline(props: {
  events: { kind: string; ts: string; payload: { phase?: string } }[];
  kind: 'change' | 'workshop';
  repo: string;
  currentPhase: string;
}) {
  /* The tick is only drawn for a phase the daemon has enough comparable
     runs to speak about, on this repo and this kind. A phase it cannot
     speak about gets no tick, rather than a number nobody should act on
     (V6 section C). The bars themselves are drawn from the daemon's own
     timestamps, which is new in V6: they used to be measured in the
     browser's receive time, so a reconnect collapsed them. */
  const [typical, setTypical] = useState<Record<string, number>>({});
  useEffect(() => {
    void api
      .get<{ typicalMin: Record<string, number> }>(
        `/api/stats/phase-typical?kind=${props.kind}&repo=${encodeURIComponent(props.repo)}`,
      )
      .then((r) => setTypical(r.typicalMin))
      .catch(() => setTypical({}));
  }, [props.kind, props.repo]);

  const spans = useMemo(() => {
    const phases = props.events.filter((e) => e.kind === 'phase' && e.payload.phase);
    const out: { phase: string; startedAt: string; minutes: number; open: boolean }[] = [];
    for (let i = 0; i < phases.length; i++) {
      const end = i + 1 < phases.length ? Date.parse(phases[i + 1].ts) : Date.now();
      out.push({
        phase: phases[i].payload.phase!,
        startedAt: phases[i].ts,
        minutes: Math.max(0, (end - Date.parse(phases[i].ts)) / 60000),
        open: i + 1 >= phases.length,
      });
    }
    return out.filter((s) => s.minutes > 0.05 || s.open);
  }, [props.events]);

  if (!spans.length) return null;
  const maxRef = Math.max(...spans.map((s) => Math.max(s.minutes, typical[s.phase] ?? 0)), 1);
  return (
    <div className="ops-timeline" data-testid="phase-timeline">
      {spans.map((s, i) => {
        const t = typical[s.phase];
        return (
          <div key={`${s.phase}-${i}`} className="ops-timeline__row">
            <span className="ops-timeline__label">{s.phase.replace(/_/g, ' ')}</span>
            <span className="ops-timeline__bars">
              <span
                className={`ops-timeline__bar${s.open ? ' ops-timeline__bar--live' : ''}`}
                style={{ width: `${Math.max(2, (s.minutes / maxRef) * 100)}%` }}
              />
              {t ? (
                <span className="ops-timeline__tick" style={{ left: `${Math.min(100, (t / maxRef) * 100)}%` }} title={`typical ${t} min`} />
              ) : null}
            </span>
            <span className="ops-timeline__min">
              {s.minutes < 1 ? `${Math.round(s.minutes * 60)}s` : `${Math.round(s.minutes)}m`}
              {t ? <em> / ~{t < 1 ? `${Math.round(t * 60)}s` : `${Math.round(t)}m`}</em> : null}
            </span>
          </div>
        );
      })}
    </div>
  );
}

// ── Cost ────────────────────────────────────────────────────────────────────
export function CostPanel(props: { jobId: string; refreshKey: number }) {
  const [data, setData] = useState<{
    totalUsd: number;
    perPhase: Record<string, number>;
    tokens: { input: number; output: number; cacheRead: number; cacheCreate: number };
    perModel: Record<string, number>;
    turns: number;
    measured: boolean;
  } | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    setError(null);
    void api
      .get<NonNullable<typeof data>>(`/api/jobs/${props.jobId}/cost`)
      .then(setData)
      .catch((e: unknown) => setError(e instanceof Error ? e.message : 'The cost did not load.'));
  }, [props.jobId, props.refreshKey]);
  if (error) {
    return (
      <div className="ops-card" data-testid="cost-panel">
        <p style={{ margin: 0, fontSize: 13 }}>{error}</p>
      </div>
    );
  }
  if (!data) return null;
  /* Nothing has reported a cost yet is a different fact from this cost
     nothing, and printing $0.00 for the first is how a job that spent $416
     read as free (V6 section C). */
  if (!data.measured) {
    return (
      <div className="ops-card" data-testid="cost-panel">
        <p style={{ margin: 0, fontSize: 13, color: 'var(--ops-ink-subtle)' }}>
          No turn has reported a cost yet. Nothing has been measured to show.
        </p>
      </div>
    );
  }
  const phases = Object.entries(data.perPhase).filter(([, v]) => v > 0);
  const max = Math.max(...phases.map(([, v]) => v), 0.01);
  const fmt = (n: number) => (n >= 1e6 ? `${(n / 1e6).toFixed(1)}M` : n >= 1e3 ? `${(n / 1e3).toFixed(0)}k` : String(n));
  return (
    <div className="ops-card" data-testid="cost-panel">
      <p className="ops-cost__total" data-testid="cost-total">
        <b>${data.totalUsd.toFixed(2)}</b> over {data.turns} {data.turns === 1 ? 'turn' : 'turns'}
      </p>
      {phases.map(([phase, usd]) => (
        <div key={phase} className="ops-timeline__row">
          <span className="ops-timeline__label">{phase.replace(/_/g, ' ')}</span>
          <span className="ops-timeline__bars">
            <span className="ops-timeline__bar" style={{ width: `${Math.max(2, (usd / max) * 100)}%` }} />
          </span>
          <span className="ops-timeline__min">${usd.toFixed(2)}</span>
        </div>
      ))}
      <div className="ops-tokenrow">
        <span>
          <b>{fmt(data.tokens.input)}</b> in
        </span>
        <span>
          <b>{fmt(data.tokens.output)}</b> out
        </span>
        <span>
          <b>{fmt(data.tokens.cacheRead)}</b> cache read
        </span>
        <span>
          <b>{fmt(data.tokens.cacheCreate)}</b> cache write
        </span>
      </div>
      {Object.keys(data.perModel).length > 1 ? (
        <div className="ops-tokenrow">
          {Object.entries(data.perModel).map(([m, usd]) => (
            <span key={m}>
              <b>${usd.toFixed(2)}</b> {m.replace('claude-', '')}
            </span>
          ))}
        </div>
      ) : null}
    </div>
  );
}

// ── Diff ────────────────────────────────────────────────────────────────────
export function DiffList(props: { jobId: string; files: { file: string; additions: number; deletions: number }[] }) {
  const [open, setOpen] = useState<string | null>(null);
  const [patch, setPatch] = useState('');
  useEffect(() => {
    if (!open) return;
    setPatch('Reading…');
    void api
      .get<{ patch: string }>(`/api/jobs/${props.jobId}/diff/file?path=${encodeURIComponent(open)}`)
      .then((r) => setPatch(r.patch || 'No diff readable for this file.'))
      .catch(() => setPatch('Could not read the diff.'));
  }, [open, props.jobId]);
  return (
    <div className="ops-card" data-testid="diff-list">
      {props.files.map((f) => (
        <div key={f.file}>
          <button type="button" className="ops-diffrow" onClick={() => setOpen(open === f.file ? null : f.file)}>
            {open === f.file ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
            <span className="ops-diffrow__file">{f.file}</span>
            <span className="ops-diffrow__counts">
              <em>+{f.additions}</em> <s>-{f.deletions}</s>
            </span>
          </button>
          {open === f.file ? (
            <pre className="ops-diffpatch">
              {patch.split('\n').map((line, i) => (
                <span
                  key={i}
                  className={
                    line.startsWith('+') && !line.startsWith('+++')
                      ? 'ops-diffpatch__add'
                      : line.startsWith('-') && !line.startsWith('---')
                        ? 'ops-diffpatch__del'
                        : line.startsWith('@@')
                          ? 'ops-diffpatch__hunk'
                          : undefined
                  }
                >
                  {line}
                  {'\n'}
                </span>
              ))}
            </pre>
          ) : null}
        </div>
      ))}
    </div>
  );
}
// ── Artifacts (what a workshop delivered) ───────────────────────────────────
export interface ArtifactRow {
  id: string;
  filename: string;
  size: number;
  mime: string | null;
}

export function ArtifactsPanel(props: { jobId: string; artifacts: ArtifactRow[] }) {
  const [report, setReport] = useState<string | null>(null);
  const reportRow = props.artifacts.find((a) => a.filename === 'REPORT.md');
  useEffect(() => {
    if (!reportRow) return setReport(null);
    void fetch(`/api/jobs/${props.jobId}/artifacts/${reportRow.id}`)
      .then((r) => (r.ok ? r.text() : null))
      .then(setReport)
      .catch(() => setReport(null));
  }, [props.jobId, reportRow?.id, reportRow]);

  const fmtSize = (n: number) => (n < 1024 ? `${n} B` : n < 1048576 ? `${Math.round(n / 102.4) / 10} KB` : `${Math.round(n / 104857.6) / 10} MB`);
  return (
    <div data-testid="artifacts">
      {report ? (
        <div className="ops-card ops-report">
          <pre>{report}</pre>
        </div>
      ) : null}
      <div className="ops-rows" style={report ? { marginTop: 10 } : undefined}>
        {props.artifacts.map((a) => (
          <div key={a.id} className="ops-row">
            <div className="ops-row__main">
              <div className="ops-row__title">{a.filename}</div>
              <div className="ops-row__sub">{fmtSize(a.size)}</div>
            </div>
            <div className="ops-row__end">
              <a
                className="ops-btn ops-btn--ghost"
                href={`/api/jobs/${props.jobId}/artifacts/${a.id}?download=1`}
                aria-label={`Download ${a.filename}`}
              >
                <Download size={16} />
              </a>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export function CommitList(props: { commits: { sha: string; subject: string; localOnly?: boolean }[] }) {
  return (
    <div className="ops-card" data-testid="commit-list">
      {props.commits.map((c) => (
        <div key={c.sha} className="ops-ci__row" data-local-only={c.localOnly ? 'true' : undefined}>
          <Sha value={c.sha} />
          <span className="ops-ci__name" style={{ fontFamily: 'var(--ops-font)', fontSize: 13 }}>{c.subject}</span>
          {c.localOnly ? <Badge tone="hold">only on the box</Badge> : null}
        </div>
      ))}
    </div>
  );
}

/* The deploy run, read from GitHub (Ops V7 design §6): the demo repository
   deploys itself on main; Meridian rides release.yml to staging and is
   stamped verified. A fact with an age, never a wait. */
export function DeployPanel(props: { deploy: DeployFacts }) {
  const d = props.deploy;
  const ok = d.kind === 'pages' ? d.conclusion === 'success' : Boolean(d.verifiedSha);
  const bad = d.conclusion === 'failure';
  const running = d.status && d.status !== 'completed';
  const label = ok ? (d.kind === 'pages' ? 'Live' : 'Staging verified') : bad ? 'Failed' : running ? 'Running' : d.status ? d.conclusion ?? d.status : 'No run yet';
  return (
    <div className="ops-card" data-testid="deploy-panel">
      <div className="ops-deploy__head">
        <Badge tone={ok ? 'ok' : bad ? 'bad' : running ? 'run' : 'wait'} pulse={Boolean(running)}>
          {label}
        </Badge>
        <span className="ops-deploy__project">
          {d.kind === 'pages' ? 'GitHub Actions → Cloudflare Pages' : 'release.yml → staging'}
          {d.verifiedSha ? <> · <Sha value={d.verifiedSha} /></> : null}
          {d.refreshedAt ? ` · read ${new Date(d.refreshedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}` : ''}
        </span>
      </div>
      <div className="ops-deploy__links">
        {d.liveUrl && ok ? (
          <a className="ops-linklike" href={d.liveUrl} target="_blank" rel="noreferrer">
            {d.liveUrl.replace('https://', '')}
          </a>
        ) : null}
        {d.url ? (
          <a className="ops-linklike" href={d.url} target="_blank" rel="noreferrer">
            the run
          </a>
        ) : null}
      </div>
      {!d.status ? (
        <p className="ops-hint" style={{ margin: '8px 0 0' }}>
          No run for this merge has been seen yet. "Read again" asks GitHub.
        </p>
      ) : null}
    </div>
  );
}

// ── The daemon's own account of this job (V6 section K) ─────────────────────
/* The last two incidents each took a day to reconstruct, and both
 * reconstructions were done over SSH against systemd and a stderr file
 * nothing in the app had ever exposed. There is no reason for that: the
 * daemon knows what it did, and this is where it says so. Fetched only when
 * opened, so an ordinary job page never pays for it. */
export function JournalPanel(props: { jobId: string }) {
  const [data, setData] = useState<{ unit: string | null; journal: string; stderr: string } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState(false);

  const load = () => {
    setError(null);
    void api
      .get<NonNullable<typeof data>>(`/api/jobs/${props.jobId}/journal`)
      .then(setData)
      .catch((e: unknown) => setError(e instanceof Error ? e.message : 'The journal did not load.'));
  };

  if (!open) {
    return (
      <button
        type="button"
        className="ops-journal__open"
        data-testid="open-journal"
        onClick={() => {
          setOpen(true);
          load();
        }}
      >
        Read the daemon's own log for this job
      </button>
    );
  }
  return (
    <div className="ops-card" data-testid="journal-panel">
      {error ? (
        <p style={{ margin: 0, fontSize: 13 }}>
          {error}{' '}
          <button type="button" className="ops-journal__retry" onClick={load}>
            Try again
          </button>
        </p>
      ) : !data ? (
        <p style={{ margin: 0, fontSize: 13, color: 'var(--ops-ink-subtle)' }}>Fetching…</p>
      ) : (
        <>
          <p className="ops-mono-sub">{data.unit ?? 'no session unit'}</p>
          {data.stderr ? (
            <>
              <h5 className="ops-journal__head">What the session printed to stderr</h5>
              <pre className="ops-journal__pre" data-testid="journal-stderr">
                {data.stderr}
              </pre>
            </>
          ) : null}
          <h5 className="ops-journal__head">The unit's journal</h5>
          <pre className="ops-journal__pre" data-testid="journal-body">
            {data.journal || 'Nothing recorded.'}
          </pre>
        </>
      )}
    </div>
  );
}
